import { Component, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { merge, Subscription } from 'rxjs';
import { SUPER_ADMIN_LEVEL } from '../../constants/access-levels';
import { ConfigListEntry } from '../../shared/app-config.model';
import { ConfigService } from '../../services/config.service';
import { environment } from '../../../environments/environment';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { GlobalsService } from '../../services/globals.service';
import { expandConfigKeysByCompanyKey, parseAllowedClientConfigKeysFromUser } from '../../utils/config-list-expand';

@Component({
	selector: 'app-config-switcher',
	templateUrl: './config-switcher.component.html',
	styleUrls: ['./config-switcher.component.scss']
})
export class ConfigSwitcherComponent implements OnInit, OnDestroy {
	availableConfigs: ConfigListEntry[] = [];
	currentConfig: any = '';
	selectedConfig: any = ''
	user: any = [];
	@Output() reload = new EventEmitter < boolean > ();
	private subscriptions: Subscription = new Subscription();
	private fullConfigList: ConfigListEntry[] = [];

	constructor(
		private globalsService: GlobalsService,
		private globalSearchService: GlobalSearchService,
		private configService: ConfigService
	) {
		this.globalSearchService.user.subscribe(r => {
			this.user = r;
		});
	}

	ngOnInit(): void {
		this.subscriptions.add(
			this.configService.loadAvailableConfigs().subscribe(
				configs => {
					this.fullConfigList = configs || [];
					this.applyAvailableConfigsFilter();
				},
				error => console.error('Error loading available configs', error)
			)
		);

		this.subscriptions.add(
			merge(
				this.globalSearchService.user,
				this.globalSearchService.configsubscription
			).subscribe(() => this.applyAvailableConfigsFilter())
		);
	}

	private applyAvailableConfigsFilter(): void {
		if (!this.fullConfigList.length) {
			this.availableConfigs = [];
			return;
		}

		const cfg = this.globalSearchService.config;
		const u = this.globalSearchService.user.getValue();

		// env.client is often the same for prod + play (e.g. both "km"); the real setup is the file key (km vs kmproca).
		const serviceKey = this.configService.getCurrentConfig();
		const envClient = cfg?.env?.client ?? environment.name;
		if (serviceKey && this.fullConfigList.some((e) => e.config === serviceKey)) {
			this.currentConfig = serviceKey;
		} else {
			this.currentConfig = envClient;
		}

		const level = u?.user?.fullaccess != null ? parseInt(String(u.user.fullaccess), 10) : NaN;
		if (Number.isNaN(level)) {
			this.availableConfigs = [];
			return;
		}

		if (level >= SUPER_ADMIN_LEVEL) {
			this.availableConfigs = this.fullConfigList.filter((config) =>
				level >= (config.allowedAccessLevel ?? SUPER_ADMIN_LEVEL)
			);
			this.syncSelectionToAllowedList(envClient);
			return;
		}

		// Scope must follow the *loaded* config (env.client), not the stored file key — a stale v8ActiveSetupKey
		// would point at another company's row and filter the list to zero (dropdown disappears).
		let active = this.fullConfigList.find((e) => e.config === envClient);
		if (!active && serviceKey) {
			active = this.fullConfigList.find((e) => e.config === serviceKey);
		}
		const scope = active?.companyKey;
		if (!scope) {
			this.availableConfigs = [];
			return;
		}

		const allowedKeys = parseAllowedClientConfigKeysFromUser(u?.user);
		const hasAllowlist = allowedKeys.length > 0;
		const expandedKeys = hasAllowlist ? expandConfigKeysByCompanyKey(allowedKeys, this.fullConfigList) : [];
		const allow = hasAllowlist ? new Set(expandedKeys) : null;

		if (hasAllowlist) {
			this.availableConfigs = this.fullConfigList.filter(
				(c) => c.companyKey === scope && allow!.has(c.config)
			);
			this.syncSelectionToAllowedList(envClient);
			return;
		}

		this.availableConfigs = this.fullConfigList.filter(
			(config) =>
				config.companyKey === scope &&
				level >= (config.allowedAccessLevel ?? SUPER_ADMIN_LEVEL)
		);
		this.syncSelectionToAllowedList(envClient);
	}

	/** If stored file key is not in the allowed list (stale tenant), show env.client or first option. */
	private syncSelectionToAllowedList(envClient: string): void {
		if (!this.availableConfigs.length) {
			return;
		}
		if (this.availableConfigs.some((c) => c.config === this.currentConfig)) {
			return;
		}
		let next = envClient;
		if (!this.availableConfigs.some((c) => c.config === next)) {
			next = this.availableConfigs[0].config;
		}
		this.currentConfig = next;
		this.configService.setActiveSetupKey(next);
	}

	onChangeConfig(newConfig: string): void {
		this.currentConfig = newConfig;

		const loadConfigSubscription = this.configService.loadConfig(newConfig).subscribe(
			r => {
				this.globalSearchService.setConfig(r);
				this.globalsService.updateConfigFile(r);
				this.globalSearchService.reload();
				this.reload.emit(true);
			},
			error => {
				console.error('Error loading configuration', error);
			}
		);

		this.subscriptions.add(loadConfigSubscription);
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}
}
